import { useState, useRef, useCallback, useEffect, RefObject } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePost } from '../../../../../../API';
import { copyToClipboard } from '../../../../../../Util';
import { useToast } from '@aidkitorg/component-library';
import { getURLsFromCSV } from '@aidkitorg/roboscreener/lib/util/urls_from_csv';
import { useLocalizedStrings } from '../../../../../../Localization';
import { SCALE_FACTOR } from './constants';

// Hook to get signed URL for first attachment (used for selfie)
export function useFirstSignedURL(attachments?: string) {
  const getSignedURL = usePost('/document/view');
  const urls = attachments ? getURLsFromCSV(attachments) : [];
  const firstUrl = urls[0];

  const { data: signedURL } = useQuery({
    queryKey: ['firstSignedUrl', firstUrl],
    queryFn: async () => {
      const paths = await getSignedURL({ paths: [firstUrl] });
      return paths?.paths?.[firstUrl] || '';
    },
    enabled: !!attachments && !!firstUrl,
    staleTime: 5 * 60 * 1000, // Consider signed URLs fresh for 5 minutes
  });

  return signedURL || '';
}

// Hook to get all signed URLs for attachments
export function useSignedURLs(attachments?: string[]) {
  const getSignedURL = usePost('/document/view');

  const { data: signedURLs } = useQuery({
    queryKey: ['signedUrls', attachments],
    queryFn: async () => {
      if (!attachments || attachments.length === 0) return {};
      const paths = await getSignedURL({ paths: attachments });
      return paths?.paths || {};
    },
    enabled: !!attachments && attachments.length > 0,
    staleTime: 5 * 60 * 1000, // Consider signed URLs fresh for 5 minutes
  });

  return signedURLs || {};
}

// Hook for copy-to-clipboard functionality
export function useCopyToClipboard(text: string) {
  const [copied, setCopied] = useState<boolean>(false);
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleCopyText = useCallback((textOverride?: string) => {
    if (copied) return;
    try {
      const textToCopy = textOverride || text || '';
      copyToClipboard(textToCopy);
      setCopied(true);
      // Clear any existing timeout
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      // Set new timeout
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setCopied(false);
    }
  }, [copied, text]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  return { copied, handleCopyText };
}

// Hook for delete message mutation
export function useDeleteMessage() {
  const L = useLocalizedStrings();
  const toast = useToast();
  const queryClient = useQueryClient();
  const deleteMessage = usePost('/messages/delete_messages');

  return useMutation({
    mutationFn: async ({ messageId, reason }: { messageId: string; reason: string }) => {
      await deleteMessage({ messageIds: [messageId], reason });
    },
    onSuccess: () => {
      // Invalidate messages query to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['supportMessages'] });
      toast.toast({
        description: L.applicant.comms.message_deleted,
        variant: 'success'
      });
    },
    onError: () => {
      toast.toast({
        variant: 'error',
        title: L.applicant.comms.failed_to_delete_message
      });
    }
  });
}

// Hook for delete attachments mutation
export function useDeleteAttachments() {
  const L = useLocalizedStrings();
  const toast = useToast();
  const queryClient = useQueryClient();
  const deleteAttachments = usePost('/messages/delete_attachments');

  return useMutation({
    mutationFn: async ({ messageId, reason }: { messageId: string; reason: string }) => {
      await deleteAttachments({
        messageIds: [messageId],
        reason,
      });
    },
    onSuccess: () => {
      // Invalidate messages query to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['supportMessages'] });
      toast.toast({
        description: L.applicant.comms.attachments_deleted_success,
        variant: 'success'
      });
    },
    onError: () => {
      toast.toast({
        variant: 'error',
        title: L.applicant.comms.failed_to_delete_attachments
      });
    }
  });
}

// Hook for delete recording mutation
export function useDeleteRecording() {
  const L = useLocalizedStrings();
  const toast = useToast();
  const queryClient = useQueryClient();
  const deleteRecording = usePost('/messages/delete_recordings');

  return useMutation({
    mutationFn: async ({ messageId, reason }: { messageId: string; reason: string }) => {
      await deleteRecording({
        messageIds: [messageId],
        reason,
      });
    },
    onSuccess: () => {
      // Invalidate messages query to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['supportMessages'] });
      toast.toast({
        description: L.applicant.comms.recording_deleted_success,
        variant: 'success'
      });
    },
    onError: () => {
      toast.toast({
        variant: 'error',
        title: L.applicant.comms.failed_to_delete_recording
      });
    }
  });
}

// Hook for image manipulation controls
export function useImageControls(ref: RefObject<HTMLImageElement>) {
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hasError, setHasError] = useState(false);

  const handleImageError = useCallback(() => {
    setHasError(true);
  }, []);

  const createHandler = useCallback((handler: () => void) => (e: React.MouseEvent) => {
    handler();
    e.preventDefault();
    e.stopPropagation();
    return false;
  }, []);

  const rotate = useCallback(() => {
    setRotation((rotation) => (rotation + 90) % 360);
    setPosition({ x: 0, y: 0 }); // Reset position on rotation
  }, []);

  const zoomIn = useCallback(() => {
    setScale((scale) => scale * SCALE_FACTOR);
  }, []);

  const zoomOut = useCallback(() => {
    setScale((scale) => {
      const newScale = scale / SCALE_FACTOR;
      // Reset position if zooming out to 1 or less
      if (newScale <= 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newScale;
    });
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
      e.preventDefault();
    }
  }, [scale, position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  }, [isDragging, dragStart, scale]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  return {
    rotation,
    scale,
    position,
    isDragging,
    hasError,
    handleImageError,
    createHandler,
    rotate,
    zoomIn,
    zoomOut,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
  };
}
